import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_WORKFLOW_EMAIL_TEMPLATES } from '@/lib/constants/api'
import getWorkflowEmailTemplateModel from './models/workflowEmailTemplate'
import updateStatus from '@/store/helpers/updateStatus'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const workflowEmailTemplate = {
  namespaced: true,
  state: {
    ...getWorkflowEmailTemplateModel()
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    clearWorkflowEmailTemplate: (state) => {
      const model = getWorkflowEmailTemplateModel()
      Object.keys(model).forEach(key => { state[key] = model[key] })
    },
    setWorkflowEmailTemplate: (state, template) => {
      state.id = template.id
      state.name = template.name
      state.clientId = template.clientId
      state.clientName = template.clientName
      state.subjectLine = template.subjectLine
      state.bodyHtml = template.bodyHtml
      state.recipientSourceId = template.recipientSourceId
      state.recipientSourceName = template.recipientSourceName
      state.customRecipients = template.customRecipients
      state.additionalRecipients = template.additionalRecipients
      state.reviewerEmails = template.reviewerEmails
      state.isActive = template.isActive
      state.attachments = template.attachments || []
      state.createdBy = template.createdBy
      state.updatedBy = template.updatedBy
      state.createdAt = template.createdAt
      state.updatedAt = template.updatedAt
      state.deactivatedByUserId = template.deactivatedByUserId
      state.deactivatedAt = template.deactivatedAt
    }
  },
  actions: {
    save ({ state }) {
      const { name, clientId, subjectLine, bodyHtml, recipientSourceId, customRecipients, additionalRecipients, reviewerEmails, isActive, attachments } = state
      return api.doPost({
        path: PATH_WORKFLOW_EMAIL_TEMPLATES,
        data: { name, clientId, subjectLine, bodyHtml, recipientSourceId, customRecipients, additionalRecipients, reviewerEmails, isActive, attachments }
      })
    },
    update ({ state }) {
      const { id, name, clientId, subjectLine, bodyHtml, recipientSourceId, customRecipients, additionalRecipients, reviewerEmails, isActive, attachments } = state
      return api.doPut({
        path: PATH_WORKFLOW_EMAIL_TEMPLATES,
        data: { id, name, clientId, subjectLine, bodyHtml, recipientSourceId, customRecipients, additionalRecipients, reviewerEmails, isActive, attachments }
      })
    },
    getWorkflowEmailTemplate: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading template, please wait...' })

      return api.doGet({ path: `${PATH_WORKFLOW_EMAIL_TEMPLATES}/${id}` })
        .then(json => json.data)
        .then(template => {
          commit('setWorkflowEmailTemplate', template)
          commit('updateStatus', { status: COMPLETE })
          return template
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveWorkflowEmailTemplate: ({ state, commit, dispatch }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving template, please wait...' })

      const actionName = state.id ? 'update' : 'save'

      return dispatch(actionName).then(response => {
        if (!response.data) {
          if (response.errorMessage) {
            throw new Error(response.errorMessage)
          }
          throw new Error('Unable to save template. Check required fields.')
        }

        commit('setWorkflowEmailTemplate', response.data)
        commit('updateStatus', { status: COMPLETE })
        notifySuccess(dispatch, 'Template saved')

        return response
      }).catch((error) => {
        commit('updateStatus', { status: ERROR, error })
        notifyError(dispatch, error || 'Unable to save template. Try again later')
        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    copyWorkflowEmailTemplate: ({ dispatch }, { sourceTemplateId, name, clientId }) => {
      showProgressModal({ dispatch, message: 'Copying template, please wait...' })
      return api.doPost({
        path: `${PATH_WORKFLOW_EMAIL_TEMPLATES}/${sourceTemplateId}/copy`,
        data: { name, clientId }
      })
        .then(response => {
          if (!response.data) {
            throw new Error('Unable to copy email template for this client.')
          }
          return response.data
        })
        .catch(error => {
          notifyError(dispatch, error || 'Unable to copy email template')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearWorkflowEmailTemplate: ({ commit }) => {
      commit('clearWorkflowEmailTemplate')
      return commit('updateStatus', { status: NOT_STARTED })
    }
  }
}

export default workflowEmailTemplate
